import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import {
  type AskAiResponse,
  type CopilotAnomaliesResponse,
  type CopilotSettings,
  askAi,
  getCopilotAnomalies,
  getCopilotSettings,
} from '../lib/api';

/**
 * Copilot home page. Shows:
 *   - Today's AI anomaly alerts (cached read).
 *   - Free-form Ask AI panel.
 *   - Quick-links to per-feature settings.
 */
export function CopilotPage(): ReactElement {
  const [settings, setSettings] = useState<CopilotSettings | null>(null);
  const [anomalies, setAnomalies] = useState<CopilotAnomaliesResponse | null>(null);
  const [askQuestion, setAskQuestion] = useState('');
  const [askResult, setAskResult] = useState<AskAiResponse | null>(null);
  const [askLoading, setAskLoading] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);

  useEffect(() => {
    getCopilotSettings()
      .then(setSettings)
      .catch(() => {});
    getCopilotAnomalies()
      .then(setAnomalies)
      .catch(() => setAnomalies({ date: '', anomalies: [], cached: false }));
  }, []);

  const onAsk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!askQuestion.trim()) return;
    setAskLoading(true);
    setAskError(null);
    setAskResult(null);
    try {
      const r = await askAi(askQuestion);
      setAskResult(r);
    } catch (err) {
      setAskError(err instanceof Error ? err.message : 'request failed');
    } finally {
      setAskLoading(false);
    }
  };

  const aiConfigured = settings?.aiConfigured ?? false;

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <header className="mb-6">
        <h1 className="text-3xl font-semibold">AI Ops Copilot</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tenant-scoped Claude assistant for daily briefings, customer replies, no-show prediction,
          anomaly detection, and free-form questions.
        </p>
      </header>

      {!aiConfigured && (
        <div className="mb-6 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          AI Copilot is not enabled. Set <code>ANTHROPIC_API_KEY</code> via{' '}
          <code>wrangler secret put ANTHROPIC_API_KEY</code> to enable.
        </div>
      )}

      <section className="mb-8">
        <h2 className="mb-2 text-xl font-medium">Today's anomalies</h2>
        {anomalies === null && <p className="text-sm text-muted-foreground">Loading…</p>}
        {anomalies && anomalies.anomalies.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No anomalies flagged for {anomalies.date || 'today'}.
          </p>
        )}
        {anomalies && anomalies.anomalies.length > 0 && (
          <ul className="space-y-2">
            {anomalies.anomalies.map((a) => (
              <li key={a.key} className="rounded border border-border bg-card px-4 py-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{a.key.replace(/_/g, ' ')}</span>
                  <span
                    className={`text-xs ${a.direction === 'up' ? 'text-emerald-700' : 'text-red-700'}`}
                  >
                    {a.direction === 'up' ? '↑' : '↓'} today
                  </span>
                </div>
                <p className="mt-1">{a.message}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Today: <span className="num">{a.today}</span> · 28d mean:{' '}
                  <span className="num">{a.trailing28dMean.toFixed(2)}</span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-8">
        <h2 className="mb-2 text-xl font-medium">Ask AI</h2>
        <form onSubmit={onAsk} className="space-y-2">
          <textarea
            value={askQuestion}
            onChange={(e) => setAskQuestion(e.target.value)}
            placeholder="e.g. How many boarding stays did we have last week? Or: Which customers haven't booked in 6 months?"
            className="w-full rounded border border-border bg-card px-3 py-2 text-sm"
            rows={3}
            disabled={askLoading || !aiConfigured}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Uses curated read-only tools. No arbitrary SQL.
            </p>
            <button
              type="submit"
              disabled={askLoading || !aiConfigured || !askQuestion.trim()}
              className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {askLoading ? 'Thinking…' : 'Ask'}
            </button>
          </div>
        </form>
        {askError && <p className="mt-2 text-sm text-red-700">{askError}</p>}
        {askResult && (
          <div className="mt-4 rounded border border-border bg-card p-4 text-sm">
            <p className="whitespace-pre-wrap">{askResult.answer}</p>
            {askResult.toolCallCount > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                Used <span className="num">{askResult.toolCallCount}</span> tool
                {askResult.toolCallCount === 1 ? '' : 's'}:{' '}
                {askResult.toolCalls.map((t) => t.name).join(', ')}
              </p>
            )}
          </div>
        )}
      </section>

      {settings && (
        <section className="text-sm text-muted-foreground">
          Monthly token usage:{' '}
          <span className="num">{settings.tokensUsedThisMonth.toLocaleString()}</span> /{' '}
          <span className="num">{settings.monthlyTokenCap.toLocaleString()}</span>.{' '}
          <a href="/settings/copilot" className="underline">
            Manage settings
          </a>
          .
        </section>
      )}
    </div>
  );
}
